import {ApiError} from "@variocube/app-ui";
import React, {Fragment, PropsWithChildren, useEffect, useRef} from "react";
import {useLogin} from "./auth";
import {FullScreenErrorAlert} from "./controls";
import {useLastUserEmail} from "./hooks/useLastUserEmail";
import {LoginPending} from "./LoginPending";

interface LoginStateProps {
	tenantId?: string;
	loading: boolean;
	error: any;
}

export function LoginState({loading, error, tenantId, children}: PropsWithChildren<LoginStateProps>) {
	const login = useLogin();
	const [lastEmail] = useLastUserEmail();
	const hasRedirected = useRef(false);

	const isUnauthenticated = error instanceof ApiError && error.status == 401;

	useEffect(() => {
		if (isUnauthenticated && !hasRedirected.current) {
			hasRedirected.current = true;
			login({tenantId, email: lastEmail});
		}
		// Exclude `login` from deps — it's recreated on every route change via useMemo in
		// AuthenticationProvider, which would re-trigger this effect unnecessarily.
		// The ref guard provides the actual protection against duplicate redirects.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isUnauthenticated, tenantId, lastEmail]);

	if (loading) {
		return <LoginPending />;
	}
	if (error) {
		if (isUnauthenticated) {
			return <LoginPending />; // Show loading while redirecting to IDP
		}
		return <FullScreenErrorAlert error={error} />;
	}
	return <Fragment>{children}</Fragment>;
}
